// ============================================================================
// ClientReportPage — routes /app/reports/client AND /app/clients/:id/report
// (namespace: "clientReport")
// ----------------------------------------------------------------------------
// The in-app, premium per-client PROGRESS REPORT a trainer is proud to show a
// client. Consumes GET /api/reports/clients/{id}?period= via the typed API
// client + React Query hook (useClientReport); live-sync (AppShell) invalidates
// qk.reports.* on any measurement/session/goal/client change so this refreshes
// across devices with no reload.
//
// Behaviour:
//   • /app/reports/client (no :id) → a client picker, then navigate per-client.
//   • /app/clients/:id/report      → the report for that client.
//   • Period selector (all/month/quarter/year) re-fetches the same report.
//   • Each section renders ONLY if it has data (graceful "not enough data yet").
//   • Download PDF hits the existing server route /clients/{id}/report.pdf.
//   • Mobile-first (designed at 390), rich Framer-Motion animation, fully
//     reduced-motion aware; built entirely on the locked Pulse design system.
//
// Keep this a DEFAULT export named ClientReportPage.
// ============================================================================
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ApiError } from "../../api";
import { useClientReport } from "../../hooks/reports";
import { EmptyState } from "../../components";
import { LoadingBlock, ErrorBlock } from "../../app/PageState";
import { IconChevronLeft } from "../../components/icons";

import { ClientPicker } from "./ClientPicker";
import { ReportHeader } from "./ReportHeader";
import { OverviewSection } from "./sections/OverviewSection";
import { BodySection } from "./sections/BodySection";
import { RatiosSection } from "./sections/RatiosSection";
import { StrengthSection } from "./sections/StrengthSection";
import { AdherenceSection } from "./sections/AdherenceSection";
import { GoalsSection } from "./sections/GoalsSection";
import { hasAnyData, isReportPeriod, parseReport } from "./types";
import type { ReportPeriod } from "./types";

export default function ClientReportPage() {
  const { id } = useParams<{ id: string }>();
  // No :id on /app/reports/client — let the trainer pick a client first.
  if (id === undefined) return <ClientPicker />;
  return <ClientReport clientId={Number(id)} />;
}

function ClientReport({ clientId }: { clientId: number }) {
  const { t } = useTranslation("clientReport");
  const [period, setPeriod] = useState<ReportPeriod>("all");

  const query = useClientReport(
    Number.isFinite(clientId) ? clientId : undefined,
    period,
  );

  const report = useMemo(
    () => (query.data ? parseReport(query.data) : null),
    [query.data],
  );

  // A removed/foreign client → the API 404s. Surface a friendly not-found.
  const isNotFound =
    query.isError &&
    query.error instanceof ApiError &&
    query.error.status === 404;

  if (query.isLoading && !report) {
    return <LoadingBlock label={t("state.loading")} />;
  }

  if (isNotFound) {
    return (
      <EmptyState
        title={t("state.notFound")}
        description={t("state.notFoundBlurb")}
      />
    );
  }

  if (!report) {
    return <ErrorBlock onRetry={() => void query.refetch()} />;
  }

  const onPeriodChange = (p: string) => {
    if (isReportPeriod(p)) setPeriod(p);
  };

  const anyData = hasAnyData(report);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Back link (per-client context) */}
      <Link
        to={`/app/clients/${report.client.id}`}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-body-soft transition-colors hover:text-coral print:hidden"
      >
        <IconChevronLeft width={16} height={16} />
        {t("actions.back")}
      </Link>

      <ReportHeader
        client={report.client}
        period={period}
        onPeriodChange={onPeriodChange}
      />

      {/* Whole-report empty state when no section has data at all. */}
      {!anyData ? (
        <EmptyState title={t("empty.title")} description={t("empty.blurb")} />
      ) : (
        <div className="space-y-6">
          <OverviewSection report={report} />
          {report.body.ok && <BodySection report={report} index={1} />}
          {report.ratios.ok && <RatiosSection report={report} index={2} />}
          {report.strength.ok && <StrengthSection report={report} index={3} />}
          {report.adherence.ok && (
            <AdherenceSection report={report} index={4} />
          )}
          {report.goals.ok && <GoalsSection report={report} index={5} />}
        </div>
      )}

      <footer className="pt-2 text-center text-xs text-body-faint">
        <p>{t("footer.confidential")}</p>
        <p className="mt-1">{t("footer.generatedBy", { app: t("app.name") })}</p>
      </footer>
    </div>
  );
}
